// ============================================
// hooks/useFriends.js
// ============================================
import { useState, useEffect } from 'react';
import { 
  subscribeToFriends, 
  subscribeToFriendRequests,
  acceptFriendRequest,
  denyFriendRequest 
} from '../models/friendModel';
import { httpsCallable } from 'firebase/functions';
import { functions } from '../firebase';
import { trackEvent, AnalyticsEvents } from '../services/analyticsService';
import { ERRORS } from '../utils/constants';

export const useFriends = (userId) => {
  const [friends, setFriends] = useState([]);
  const [friendRequests, setFriendRequests] = useState([]);
  const [loadingFriends, setLoadingFriends] = useState(true);
  const [loadingRequests, setLoadingRequests] = useState(true);
  const isE2E = () => typeof window !== 'undefined' && !!window.Cypress;
  // In Cypress, we usually stub network. Allow tests to force real network
  // (so cy.intercept can simulate errors) via localStorage 'E2E_FORCE_NETWORK' = 'true'.
  const isE2ENetworkStubbed = () => {
    try {
      if (typeof window === 'undefined') return false;
      if (!window.Cypress) return false;
      const forceReal = window.localStorage.getItem('E2E_FORCE_NETWORK') === 'true';
      return !forceReal;
    } catch (_) {
      return false;
    }
  };
  
  const requestFriend = httpsCallable(functions, 'requestFriend');

  useEffect(() => {
    if (!userId) return;

    // Cypress E2E mode: read from localStorage-backed fixtures and subscribe to changes
    if (isE2E()) {
      const read = () => {
        try {
          const rawFriends = window.localStorage.getItem('e2eFriends');
          const rawReqs = window.localStorage.getItem('e2eFriendRequests');
          const f = rawFriends ? JSON.parse(rawFriends) : [];
          const r = rawReqs ? JSON.parse(rawReqs) : [];
          setFriends(Array.isArray(f) ? f : []);
          setFriendRequests(Array.isArray(r) ? r : []);
          setLoadingFriends(false);
          setLoadingRequests(false);
        } catch (_) {
          setFriends([]);
          setFriendRequests([]);
          setLoadingFriends(false);
          setLoadingRequests(false);
        }
      };
      read();
      const handler = () => read();
      window.addEventListener('e2e-friends-change', handler);
      return () => {
        window.removeEventListener('e2e-friends-change', handler);
      };
    }

    const unsubFriends = subscribeToFriends(userId, (fList) => {
      setFriends(fList);
      setLoadingFriends(false);
    });

    const unsubRequests = subscribeToFriendRequests(userId, (reqList) => {
      setFriendRequests(reqList);
      setLoadingRequests(false);
    });

    return () => {
      if (unsubFriends) unsubFriends();
      if (unsubRequests) unsubRequests();
    };
  }, [userId]);

  const sendFriendRequest = async (email) => {
    const maxAttempts = 3;
    let lastError = null;
    // In Cypress (default), simulate without hitting Firebase unless tests
    // explicitly force real network via E2E_FORCE_NETWORK
    if (isE2ENetworkStubbed()) {
      const trimmed = (email || '').trim().toLowerCase();
      if (trimmed === 'existing@example.com') {
        return { success: false, error: ERRORS.FRIEND_REQUEST_EXISTS };
      }
      // Simulate success path and optionally emit an event for any listeners
      try { window.dispatchEvent(new CustomEvent('e2e-friends-change')); } catch (_) {}
      trackEvent(AnalyticsEvents.INVITE_SENT, { method: 'email', has_email: !!email, e2e: true });
      return { success: true };
    }
    for (let attempt = 1; attempt <= maxAttempts; attempt++) {
      try {
        await requestFriend({ recipientEmail: email });
        trackEvent(AnalyticsEvents.INVITE_SENT, { method: 'email', has_email: !!email });
        return { success: true };
      } catch (error) {
        lastError = error;
        // Map quota/429 to friendly message
        const msg = String(error?.message || '');
        const code = String(error?.code || '');
        const status = Number(error?.status || error?.response?.status || 0);
        const quotaLike = /quota|limit|429/i.test(msg) || /quota|limit/i.test(code) || status === 429;
        if (quotaLike) {
          return { success: false, error: ERRORS.QUOTA_EXCEEDED };
        }
        // Retry transient (5xx, network). Firebase callable may mask HTTP status; rely on code/message too.
        const codeTransient = /unavailable|internal|unknown/i.test(code);
        const isTransient = status >= 500 || codeTransient || /unavailable|network|timeout|deadline/i.test(msg);
        if (attempt < maxAttempts && isTransient) {
          await new Promise((r) => setTimeout(r, 200 * attempt));
          continue;
        }
        break;
      }
    }
    return { success: false, error: lastError?.message || ERRORS.GENERIC_ERROR };
  };

  const acceptRequest = async (requestId) => {
    try {
      if (isE2E()) {
        // Remove the request from localStorage
        const raw = window.localStorage.getItem('e2eFriendRequests');
        const list = raw ? JSON.parse(raw) : [];
        const next = list.filter((r) => r.id !== requestId);
        window.localStorage.setItem('e2eFriendRequests', JSON.stringify(next));
        window.dispatchEvent(new CustomEvent('e2e-friends-change'));
        return { success: true };
      }
      await acceptFriendRequest(requestId, userId);
      // Analytics: invite accepted
      trackEvent(AnalyticsEvents.INVITE_ACCEPTED, { request_id: requestId });
      return { success: true };
    } catch (error) {
      return { success: false, error: error.message };
    }
  };

  const denyRequest = async (requestId) => {
    try {
      if (isE2E()) {
        const raw = window.localStorage.getItem('e2eFriendRequests');
        const list = raw ? JSON.parse(raw) : [];
        const next = list.filter((r) => r.id !== requestId);
        window.localStorage.setItem('e2eFriendRequests', JSON.stringify(next));
        window.dispatchEvent(new CustomEvent('e2e-friends-change'));
        return { success: true };
      }
      await denyFriendRequest(requestId);
      return { success: true };
    } catch (error) {
      return { success: false, error: error.message };
    }
  };

  return {
    friends,
    friendRequests,
    loadingFriends,
    loadingRequests,
    sendFriendRequest,
    acceptRequest,
    denyRequest
  };
};